import { useEffect, useState, type ReactElement, type ReactNode } from "react";
import { BODY_TYPE, COMPASS_PLACES_MOST, INTERFACE_STEPS, PARTY_INVITATIONS_OFFERED, type InterfaceStep, type OfferedInvitation, type RenderDivisor, type Size } from "odyssey-shared";
import { Button, Checkbox, KIT_METRICS, SLIDER_ROW, Segmented, Slider, centreOffset, logical, useWindowFramed, type SegmentedOption } from "../../ui/kit/index.ts";
import { levelIds, type LevelId, type VolumeLevel } from "../audio/audio-settings.ts";
import { CHAT_SIZES, type ChatSizeChoice } from "../chat/size.ts";
import { CHAT_TEXT_CHOICES, type ChatTextChoice } from "../chat/text-size.ts";
import type { BarPagingChoice } from "../settings.ts";
import { reducedMotionChoices } from "./motion-choices.ts";
import { othersNameplateChoices, ownNameplateChoices } from "./nameplate-choices.ts";
import { frameNote, frameOptions } from "./frame-choices.ts";
import { renderChoiceDivisor, renderChoiceValue, renderNote, renderOptions } from "./render-choices.ts";
import { isChanged, levelSetting, type PlainSetting, type SettingId, type SettingsDraft, type SettingsSection, type SettingsValues } from "./settings-model.ts";
import { SETTINGS_WINDOW } from "./settings-sections.ts";
import { levelLabel, menuItemName } from "./wording.ts";
import { say } from "../../words.ts";
import { zoomChoiceStep, zoomChoiceValue, zoomNote, zoomOptions } from "./zoom-choices.ts";

/**
 * Settings' panes (P1.9-T44; ADR-061's amendment "Settings in sections, and nothing applies until Apply"; the
 * design's `SettingsPane` and `SRow`, a visual reference): a section's rows, each at least 16 logical px tall
 * with its label at the left in the running-text face, its control at the right, and a 2 px mark in the focus
 * colour before the label while its pending value differs from the one in force. A row's label is its control's
 * accessible name, and a changed row's mark is its control's description. The panes draw a draft and report each change; nothing here reaches the page.
 *
 * A row is as tall as the taller of the design's 16 and its control, and its label and its control are each set
 * in the middle of that height by a whole number of logical pixels, never by CSS centring, which can land
 * between pixels; a label that wraps makes the row taller and leaves the control where it stood.
 *
 * Render resolution (ADR-093 decisions 10 and 12) has a note, a line of muted running text under it: what the
 * pending choice would cost in tiles and in the HUD's scale, or why an option is disabled. World zoom has one
 * while a step does nothing at the pending render resolution, and that step is disabled.
 */

/**
 * Chat size's choices as the segmented choice takes them: Small, Medium and Large, in that order (P1.9-T42).
 *
 * @returns The choices.
 */
function chatSizeOptions(): readonly SegmentedOption<ChatSizeChoice>[] {
  return (Object.keys(CHAT_SIZES) as ChatSizeChoice[]).map((value) => ({ value, label: say(`settings.chatSizes.${value}`) }));
}

/**
 * The language-file key of each Interface size step.
 */
const INTERFACE_WORDS = { "-2": "smallest", "-1": "smaller", 0: "auto", 1: "larger", 2: "largest" } as const satisfies Record<InterfaceStep, string>;

/**
 * Interface size's row: Smallest to Largest around Auto, whole steps of the UI scale only. A step that gives the same
 * scale as the one nearer Auto, because the window cannot hold more or the scale cannot go lower, is disabled, and the
 * note says what the window holds.
 *
 * @param props The `draft`, the page's `window`, and what the pane reports.
 * @returns The row and its note.
 */
function InterfaceSizeRow({ draft, window, events }: { readonly draft: SettingsDraft; readonly window: RenderWindow; readonly events: PaneEvents }): ReactElement | null {
  const { scaleAt, mostScale } = window;
  if (scaleAt === undefined || mostScale === undefined) {
    return null;
  }
  const id = "interfaceStep";
  const changed = isChanged(draft, id);
  const options: SegmentedOption<`${InterfaceStep}`>[] = INTERFACE_STEPS.map((step) => {
    const nearer = (step - Math.sign(step)) as InterfaceStep;
    return { value: `${step}`, label: say(`settings.interfaceSizes.${INTERFACE_WORDS[step]}`), disabled: step !== 0 && scaleAt(step) === scaleAt(nearer) };
  });
  const note = options.some((option) => option.disabled === true) ? say("settings.interfaceSizeNote", { most: String(mostScale), auto: String(scaleAt(0)) }) : null;
  return (
    <>
      <SettingsRow id={id} label={say("settings.interfaceSize")} changed={changed} control={KIT_METRICS.button.height}>
        <Segmented label={say("settings.interfaceSize")} describedBy={describedBy(id, changed, note !== null)} options={options} value={`${draft.pending.interfaceStep}`} onChange={(value) => events.onValue(id, Number(value) as InterfaceStep)} />
      </SettingsRow>
      <RowNote id={id} note={note} />
    </>
  );
}

/**
 * Chat text size's choices as the segmented choice takes them: Normal, Large, Larger and Largest (P1.9-T224).
 *
 * @returns The choices.
 */
function chatTextOptions(): readonly SegmentedOption<ChatTextChoice>[] {
  return CHAT_TEXT_CHOICES.map((value) => ({ value, label: say(`settings.chatTexts.${value}`) }));
}

/**
 * Ability bar's choices as the segmented choice takes them: All 10 and Five at a time (P1.9-T133).
 *
 * @returns The choices.
 */
function barPagingOptions(): readonly SegmentedOption<BarPagingChoice>[] {
  return (["all", "five"] as const).map((value) => ({ value, label: say(`settings.barPagings.${value}`) }));
}

/**
 * Party invitations' choices as the segmented choice takes them: On my map and Nobody, the two the game offers until the
 * friend list exists (ADR-101 decision 6).
 *
 * @returns The choices.
 */
function partyInvitesOptions(): readonly SegmentedOption<OfferedInvitation>[] {
  return PARTY_INVITATIONS_OFFERED.map((value) => ({ value, label: say(`settings.partyInviteChoices.${value}`) }));
}

/**
 * The checkbox rows: each setting that is shown or hidden, with its label.
 */
const CHECK_LABELS = {
  performance: say("settings.performance"),
  healthNumbers: say("settings.healthNumbers"),
  idleMarks: say("settings.idleMarks"),
  emotesOthers: say("settings.emotesOthers"),
  coordinates: say("settings.coordinates"),
  autoLoot: say("settings.autoLoot"),
  barKeys: say("settings.barKeys"),
  backgroundMute: say("settings.audio.background"),
  whisperBackground: say("settings.audio.whispers"),
} as const satisfies Partial<Record<PlainSetting, string>>;

/**
 * The slider's width in logical pixels, the design's.
 */
const SLIDER_WIDTH = 100;

/**
 * A row's height for a control: the design's 16, or the control's height when it is taller.
 *
 * @param control The control's height in logical pixels.
 * @returns The row's least height.
 */
export function rowHeight(control: number): number {
  return Math.max(SETTINGS_WINDOW.row, control);
}

/**
 * The id of a changed row's mark, which the row's control names as its description, so assistive technology says
 * "Changed" with the control.
 *
 * @param id The row's setting.
 * @returns The mark's element id.
 */
export function changedMarkId(id: SettingId): string {
  return `ot-settings-changed-${id}`;
}

/**
 * The id of a row's note, which the row's control names in its description.
 *
 * @param id The row's setting.
 * @returns The note's element id.
 */
export function noteId(id: SettingId): string {
  return `ot-settings-note-${id}`;
}

/**
 * A row's description for its control: the changed mark while the row is changed, and the row's note when it has
 * one.
 *
 * @param id The row's setting.
 * @param changed Whether the row is changed.
 * @param noted Whether the row has a note.
 * @returns The ids, separated by a space, or undefined for a row with neither.
 */
function describedBy(id: SettingId, changed: boolean, noted: boolean = false): string | undefined {
  const ids = [changed ? changedMarkId(id) : null, noted ? noteId(id) : null].filter((value) => value !== null);
  return ids.length === 0 ? undefined : ids.join(" ");
}

/**
 * What the Frame limit row reads from the page: the display's measured rate in Hz, or null before it is measured.
 */
export interface FrameWindow {
  readonly refresh: number | null;
}

/**
 * The window as the Render resolution row reads it: its device size and the divisors it offers.
 */
export interface RenderWindow {
  readonly device: Size;
  readonly offered: readonly RenderDivisor[];
  readonly scaleAt?: (step: InterfaceStep) => number;
  readonly mostScale?: number;
}

/**
 * One row: the changed mark while the row is changed, its label with its sub line under it when it has one, and its
 * control at the right.
 *
 * @param props The row's setting `id`, its `label`, whether it is `changed`, its control, the control's height in
 *   logical pixels (`control`), which the row centres its label against (a row whose control's parts differ in height
 *   passes the row's height and offsets each part itself), and its `sub` line, small muted text under the label as the
 *   design's Chat size has (the Ability bar's note, the author's ruling of 2026-10-09), which the control names as its
 *   description by the row's note id.
 * @returns The row.
 */
export function SettingsRow({ id, label, changed, control, sub, children }: { readonly id: SettingId; readonly label: string; readonly changed: boolean; readonly control: number; readonly sub?: string; readonly children: ReactNode }): ReactElement {
  const text = BODY_TYPE.body.lineHeight + (sub === undefined ? 0 : BODY_TYPE["body-sm"].lineHeight);
  const height = Math.max(rowHeight(control), text);
  return (
    <div className="ot-game-settings__row" data-setting={id} data-changed={changed ? "true" : "false"} style={{ minHeight: logical(height), gap: logical(SETTINGS_WINDOW.controlGap) }}>
      <div className="ot-game-settings__text" style={{ marginTop: logical(centreOffset(height, text)) }}>
        <div className="ot-game-settings__name" style={{ gap: logical(SETTINGS_WINDOW.markGap) }}>
          {changed ? <span id={changedMarkId(id)} role="img" aria-label={say("settings.changed")} className="ot-game-settings__mark" data-game-menu="changed" style={{ width: logical(SETTINGS_WINDOW.mark), height: logical(SETTINGS_WINDOW.mark) }} /> : null}
          <span className="ot-game-settings__label" aria-hidden="true">
            {label}
          </span>
        </div>
        {sub === undefined ? null : (
          <span id={noteId(id)} className="ot-game-settings__sub" aria-hidden="true" data-sub-for={id}>
            {sub}
          </span>
        )}
      </div>
      <div className="ot-game-settings__control" style={{ marginTop: logical(centreOffset(height, control)) }}>
        {children}
      </div>
    </div>
  );
}

/**
 * A level's row: its slider, heard as it moves, and its Mute, which silences the level and keeps its volume, the
 * slider reading "Off" while muted.
 *
 * @param props The level's `id`, its pending `level`, whether the row is `changed`, and `onChange` with a new
 *   level.
 * @returns The row, a group named by the level.
 */
function LevelRow({ id, level, changed, onChange }: { readonly id: LevelId; readonly level: VolumeLevel; readonly changed: boolean; readonly onChange: (level: VolumeLevel) => void }): ReactElement {
  const label = levelLabel(id);
  const height = rowHeight(Math.max(SLIDER_ROW, KIT_METRICS.check.row));
  return (
    <SettingsRow id={levelSetting(id)} label={label} changed={changed} control={height}>
      <div className="ot-game-settings__level" role="group" aria-label={label} data-audio-level={id} style={{ gap: logical(SETTINGS_WINDOW.controlGap) }}>
        <div className="ot-game-settings__part" style={{ marginTop: logical(centreOffset(height, SLIDER_ROW)) }}>
          <Slider label={say("settings.audio.volume", { level: label })} describedBy={describedBy(levelSetting(id), changed)} value={level.volume} muted={level.muted} width={SLIDER_WIDTH} onChange={(volume) => onChange({ volume, muted: level.muted })} onCommit={(volume) => onChange({ volume, muted: level.muted })} />
        </div>
        <div className="ot-game-settings__part" style={{ marginTop: logical(centreOffset(height, KIT_METRICS.check.row)) }}>
          <Checkbox label={say("settings.audio.mute")} describedBy={describedBy(levelSetting(id), changed)} checked={level.muted} onChange={(muted) => onChange({ volume: level.volume, muted })} />
        </div>
      </div>
    </SettingsRow>
  );
}

/**
 * Account's pane, for an account session: Log out everywhere, a danger button that asks in the confirm dialog
 * first, busy while the tab leaves by it and disabled while it leaves another way. It is an action, not a
 * setting: it is never marked and Apply has no part in it.
 *
 * @param props The button's `busy` and `disabled`, and `onChoose`, run when it is pressed.
 * @returns The pane's content.
 */
function AccountRows({ busy, disabled, onChoose }: { readonly busy: boolean; readonly disabled: boolean; readonly onChoose: () => void }): ReactElement {
  return (
    <div className="ot-game-settings__action" data-game-menu="account" style={{ minHeight: logical(rowHeight(KIT_METRICS.button.height)) }}>
      <Button variant="danger" busy={busy} disabled={disabled} onClick={onChoose}>
        {menuItemName("everywhere")}
      </Button>
    </div>
  );
}

/**
 * How long "Windows reset" stays on its row, in milliseconds.
 */
export const WINDOWS_RESET_SHOWN_MS = 4000;

/**
 * Interface's "Reset windows" row (ADR-061's amendment "every game window moves and resizes", item 12): a button that
 * returns every game window to its default place and size at once and says "Windows reset" beside itself for a few
 * seconds. It is an action, not a setting: it is never marked, is outside the Apply draft and acts when pressed. The
 * message is a polite live region that stays on the row while it is empty, and the one timer runs only while the
 * message shows.
 *
 * @param props `onReset`, run when the button is pressed.
 * @returns The row.
 */
function ResetWindowsRow({ onReset }: { readonly onReset: () => void }): ReactElement {
  const [done, setDone] = useState(0);
  useEffect(() => {
    if (done === 0) {
      return undefined;
    }
    const timer = setTimeout(() => setDone(0), WINDOWS_RESET_SHOWN_MS);
    return () => clearTimeout(timer);
  }, [done]);
  const height = rowHeight(KIT_METRICS.button.height);
  return (
    <div className="ot-game-settings__action" data-game-menu="reset-windows" style={{ minHeight: logical(height), gap: logical(SETTINGS_WINDOW.controlGap) }}>
      <Button
        onClick={() => {
          onReset();
          setDone((count) => count + 1);
        }}
      >
        {say("settings.resetWindows")}
      </Button>
      <span className="ot-game-settings__status" role="status" data-game-menu="windows-reset" style={{ marginTop: logical(centreOffset(height, BODY_TYPE.body.lineHeight)) }}>
        {done === 0 ? null : say("settings.windowsReset")}
      </span>
    </div>
  );
}

/**
 * What a pane reports.
 */
export interface PaneEvents {
  /**
   * A plain setting was given a new pending value.
   *
   * @param id The setting.
   * @param value The value.
   */
  readonly onValue: <K extends PlainSetting>(id: K, value: SettingsValues[K]) => void;
  /**
   * A level was moved or muted.
   *
   * @param id Master or a channel.
   * @param level The level.
   */
  readonly onLevel: (id: LevelId, level: VolumeLevel) => void;
  /** Log out everywhere was pressed. */
  readonly onEverywhere: () => void;
  /** Reset windows was pressed; a page with no windows to reset leaves it out and the row is not drawn. */
  readonly onResetWindows?: () => void;
}

/**
 * A row's note, under its row: a polite live region that stays in the pane while it holds nothing, since
 * assistive technology reads a sentence that appears in a region already there. While empty it has no height and
 * takes back the pane's gap, so the rows stand where they would without it.
 *
 * @param props The row's setting `id` and the `note`, or null for none.
 * @returns The region, holding the note when there is one.
 */
function RowNote({ id, note }: { readonly id: SettingId; readonly note: string | null }): ReactElement {
  return note === null ? (
    <div id={noteId(id)} className="ot-game-settings__note" aria-live="polite" data-note-for={id} style={{ marginTop: logical(-SETTINGS_WINDOW.rowGap) }} />
  ) : (
    <div id={noteId(id)} className="ot-game-settings__note" aria-live="polite" data-note-for={id} data-game-menu="note">
      {note}
    </div>
  );
}

/**
 * Render resolution's row: Full, Half and Third, an option the window does not offer disabled, and under the row
 * its note, when it has one.
 *
 * @param props The `draft`, the page's `window`, and what the pane reports.
 * @returns The row and its note.
 */
function RenderRow({ draft, window, events }: { readonly draft: SettingsDraft; readonly window: RenderWindow; readonly events: PaneEvents }): ReactElement {
  const id = "renderDivisor";
  const changed = isChanged(draft, id);
  const note = renderNote(window, draft.applied.renderDivisor, draft.pending.renderDivisor, draft.pending.zoom);
  return (
    <>
      <SettingsRow id={id} label={say("settings.renderResolution")} changed={changed} control={KIT_METRICS.button.height}>
        <Segmented label={say("settings.renderResolution")} describedBy={describedBy(id, changed, note !== null)} options={renderOptions(window.offered)} value={renderChoiceValue(draft.pending.renderDivisor)} onChange={(value) => events.onValue(id, renderChoiceDivisor(value))} />
      </SettingsRow>
      <RowNote id={id} note={note} />
    </>
  );
}

/**
 * Frame limit's row: 30, 60, 120 and Match display, 120 disabled unless the display is measured faster than
 * 100 Hz, and under the row its note, when it has one.
 *
 * @param props The `draft`, what the page says of the `frames` (its display's rate), and what the pane reports.
 * @returns The row and its note.
 */
function FrameRow({ draft, frames, events }: { readonly draft: SettingsDraft; readonly frames: FrameWindow; readonly events: PaneEvents }): ReactElement {
  const id = "frameLimit";
  const changed = isChanged(draft, id);
  const note = frameNote(draft.pending.frameLimit, frames.refresh);
  return (
    <>
      <SettingsRow id={id} label={say("settings.frameLimit")} changed={changed} control={KIT_METRICS.button.height}>
        <Segmented label={say("settings.frameLimit")} describedBy={describedBy(id, changed, note !== null)} options={frameOptions(frames.refresh)} value={draft.pending.frameLimit} onChange={(value) => events.onValue(id, value)} />
      </SettingsRow>
      <RowNote id={id} note={note} />
    </>
  );
}

/**
 * World zoom's row: Farther, Default and Closer, a step that changes nothing at the pending render resolution
 * disabled, with the note that says so under the row.
 *
 * @param props The `draft`, the page's `window` (null on a page with no render resolution), and what the pane
 *   reports.
 * @returns The row and its note.
 */
function ZoomRow({ draft, window, events }: { readonly draft: SettingsDraft; readonly window: RenderWindow | null; readonly events: PaneEvents }): ReactElement {
  const id = "zoom";
  const changed = isChanged(draft, id);
  const device = window === null ? null : window.device;
  const note = zoomNote(device, draft.pending.renderDivisor);
  return (
    <>
      <SettingsRow id={id} label={say("settings.zoom")} changed={changed} control={KIT_METRICS.button.height}>
        <Segmented label={say("settings.zoom")} describedBy={describedBy(id, changed, note !== null)} options={zoomOptions(device, draft.pending.renderDivisor)} value={zoomChoiceValue(draft.pending.zoom)} onChange={(value) => events.onValue(id, zoomChoiceStep(value))} />
      </SettingsRow>
      {window === null ? null : <RowNote id={id} note={note} />}
    </>
  );
}

/**
 * Places on the compass' row (P1.9-T189): a slider from 0 to 12, how many of the nearest known places the band points to,
 * reading "Off" and dimmed at 0 as a muted level does.
 *
 * @param props The `draft` and what the pane reports.
 * @returns The row.
 */
function CompassPlacesRow({ draft, events }: { readonly draft: SettingsDraft; readonly events: PaneEvents }): ReactElement {
  const id = "compassPlaces";
  const changed = isChanged(draft, id);
  const count = draft.pending.compassPlaces;
  const height = rowHeight(Math.max(SLIDER_ROW, KIT_METRICS.check.row));
  return (
    <SettingsRow id={id} label={say("settings.compassPlaces")} changed={changed} control={height}>
      <div className="ot-game-settings__level" role="group" aria-label={say("settings.compassPlaces")} style={{ gap: logical(SETTINGS_WINDOW.controlGap) }}>
        <div className="ot-game-settings__part" style={{ marginTop: logical(centreOffset(height, SLIDER_ROW)) }}>
          <Slider label={say("settings.compassPlaces")} describedBy={describedBy(id, changed)} value={count} muted={count === 0} min={0} max={COMPASS_PLACES_MOST} step={1} width={SLIDER_WIDTH} onChange={(value) => events.onValue(id, value)} onCommit={(value) => events.onValue(id, value)} />
        </div>
      </div>
    </SettingsRow>
  );
}

/**
 * One setting's row, drawn from the draft's pending value.
 *
 * @param props The setting's `id`, the `draft`, the page's `window` for Render resolution (null on a page with
 *   none, which shows no such row), its `frames` for Frame limit (likewise) and what the pane reports.
 * @returns The row, or null for an action's key, whose rows are Controls' own pane (`controls-pane.tsx`).
 */
function SettingRow({ id, draft, window, frames, events }: { readonly id: SettingId; readonly draft: SettingsDraft; readonly window: RenderWindow | null; readonly frames: FrameWindow | null; readonly events: PaneEvents }): ReactElement | null {
  const pending = draft.pending;
  const changed = isChanged(draft, id);
  switch (id) {
    case "renderDivisor":
      return window === null ? null : <RenderRow draft={draft} window={window} events={events} />;
    case "frameLimit":
      return frames === null ? null : <FrameRow draft={draft} frames={frames} events={events} />;
    case "zoom":
      return <ZoomRow draft={draft} window={window} events={events} />;
    case "othersNameplates":
      return (
        <SettingsRow id={id} label={say("settings.othersNameplates")} changed={changed} control={KIT_METRICS.button.height}>
          <Segmented label={say("settings.othersNameplates")} describedBy={describedBy(id, changed)} options={othersNameplateChoices()} value={pending.othersNameplates} onChange={(value) => events.onValue("othersNameplates", value)} />
        </SettingsRow>
      );
    case "ownNameplate":
      return (
        <SettingsRow id={id} label={say("settings.ownNameplate")} changed={changed} control={KIT_METRICS.button.height}>
          <Segmented label={say("settings.ownNameplate")} describedBy={describedBy(id, changed)} options={ownNameplateChoices()} value={pending.ownNameplate} onChange={(value) => events.onValue("ownNameplate", value)} />
        </SettingsRow>
      );
    case "barPaging":
      return (
        <SettingsRow id={id} label={say("settings.barPaging")} changed={changed} control={KIT_METRICS.button.height} sub={say("settings.barPagingNote")}>
          <Segmented label={say("settings.barPaging")} describedBy={describedBy(id, changed, true)} options={barPagingOptions()} value={pending.barPaging} onChange={(value) => events.onValue("barPaging", value)} />
        </SettingsRow>
      );
    case "partyInvites":
      return (
        <SettingsRow id={id} label={say("settings.partyInvites")} changed={changed} control={KIT_METRICS.button.height}>
          <Segmented label={say("settings.partyInvites")} describedBy={describedBy(id, changed)} options={partyInvitesOptions()} value={pending.partyInvites} onChange={(value) => events.onValue("partyInvites", value)} />
        </SettingsRow>
      );
    case "compassPlaces":
      return <CompassPlacesRow draft={draft} events={events} />;
    case "chatSize":
      return (
        <SettingsRow id={id} label={say("settings.chatSize")} changed={changed} control={KIT_METRICS.button.height}>
          <Segmented label={say("settings.chatSize")} describedBy={describedBy(id, changed)} options={chatSizeOptions()} value={pending.chatSize === "custom" ? null : pending.chatSize} onChange={(value) => events.onValue("chatSize", value)} />
        </SettingsRow>
      );
    case "reducedMotion":
      return (
        <SettingsRow id={id} label={say("settings.reducedMotion")} changed={changed} control={KIT_METRICS.button.height}>
          <Segmented label={say("settings.reducedMotion")} describedBy={describedBy(id, changed)} options={reducedMotionChoices()} value={pending.reducedMotion} onChange={(value) => events.onValue("reducedMotion", value)} />
        </SettingsRow>
      );
    case "interfaceStep":
      return window === null ? null : <InterfaceSizeRow draft={draft} window={window} events={events} />;
    case "chatText":
      return (
        <SettingsRow id={id} label={say("settings.chatText")} changed={changed} control={KIT_METRICS.button.height}>
          <Segmented label={say("settings.chatText")} describedBy={describedBy(id, changed)} options={chatTextOptions()} value={pending.chatText} onChange={(value) => events.onValue("chatText", value)} />
        </SettingsRow>
      );
    case "performance":
    case "healthNumbers":
    case "idleMarks":
    case "emotesOthers":
    case "coordinates":
    case "autoLoot":
    case "barKeys":
    case "backgroundMute":
      return (
        <SettingsRow id={id} label={CHECK_LABELS[id]} changed={changed} control={KIT_METRICS.check.row}>
          <Checkbox label={CHECK_LABELS[id]} labelHidden describedBy={describedBy(id, changed)} checked={pending[id]} onChange={(checked) => events.onValue(id, checked)} />
        </SettingsRow>
      );
    case "whisperBackground":
      return (
        <SettingsRow id={id} label={CHECK_LABELS[id]} changed={changed} control={KIT_METRICS.check.row}>
          <Checkbox label={CHECK_LABELS[id]} labelHidden describedBy={describedBy(id, changed)} checked={pending[id]} disabled={!pending.backgroundMute} onChange={(checked) => events.onValue(id, checked)} />
        </SettingsRow>
      );
    default: {
      const level = levelIds().find((candidate) => levelSetting(candidate) === id);
      if (level === undefined) {
        return null;
      }
      return <LevelRow id={level} level={pending.levels[level]} changed={changed} onChange={(next) => events.onLevel(level, next)} />;
    }
  }
}

/**
 * A section's pane: its rows, and for Account its button.
 *
 * @param props The `section`, the settings its pane shows (`rows`), the `draft` they are drawn from, the Account
 *   button's state or null for none, the page's `window` for Render resolution and its `frames` for Frame limit (each
 *   omitted or null on a page with none), and what the pane reports.
 * @returns The pane's rows.
 */
export function SettingsPane({ section, rows, draft, account, window = null, frames = null, events }: { readonly section: SettingsSection; readonly rows: readonly SettingId[]; readonly draft: SettingsDraft; readonly account: { readonly busy: boolean; readonly disabled: boolean } | null; readonly window?: RenderWindow | null; readonly frames?: FrameWindow | null; readonly events: PaneEvents }): ReactElement {
  const framed = useWindowFramed();
  return (
    <>
      {rows.map((id) => (
        <SettingRow key={id} id={id} draft={draft} window={window} frames={frames} events={events} />
      ))}
      {section === "interface" && framed && events.onResetWindows !== undefined ? <ResetWindowsRow onReset={events.onResetWindows} /> : null}
      {section === "account" && account !== null ? <AccountRows busy={account.busy} disabled={account.disabled} onChoose={events.onEverywhere} /> : null}
    </>
  );
}
